
import React, { useState, useEffect } from "react";
import { Card, Container } from "react-bootstrap";
import { FaEdit, FaTrash } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getFerry, deleteFerry } from "../../store/slices/ferry";
import DeleteModal from "./deletepopup";

export default function Ferry() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { profile } = useSelector((state) => state.auth);

  const { ferryList = [], loading } = useSelector(
    (state) => state.ferry
  );

  // =====================================
  // PERMISSION CHECK
  // =====================================

  const isStaff =
    profile?.userType === "STAFF" ||
    profile?.role === "STAFF";

  const ferryPermission = profile?.permissions?.ferry;

  const permissions = Array.isArray(ferryPermission)
    ? ferryPermission
    : [ferryPermission];

  const canView =
    !isStaff ||
    permissions.some((p) =>
      ["VIEW", "EDIT", "FULL"].includes(
        String(p || "").toUpperCase()
      )
    );

  const canEdit =
    !isStaff ||
    permissions.some((p) =>
      ["EDIT", "FULL"].includes(
        String(p || "").toUpperCase()
      )
    );

  const canDelete =
    !isStaff ||
    permissions.some(
      (p) => String(p || "").toUpperCase() === "FULL"
    );

  const canAdd = canEdit;

  // =====================================
  // STATE
  // =====================================

  const pagelimit = 25;
  const [currentPage, setCurrentPage] = useState(1);

  const [showDelete, setShowDelete] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [deleteStatus, setDeleteStatus] = useState("confirm");

  // =====================================
  // FETCH FERRY
  // =====================================

  const fetchFerrys = async () => {
    try {
      await dispatch(getFerry()).unwrap();
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (canView) {
      fetchFerrys();
    }
  }, [canView]);

  // =====================================
  // DELETE FERRY
  // =====================================

  const handleConfirmDelete = async () => {
    if (!canDelete) return;

    try {
      setDeleteStatus("loading");

      await dispatch(deleteFerry(selectedId)).unwrap();

      setDeleteStatus("success");

      setTimeout(() => {
        setShowDelete(false);
        setDeleteStatus("confirm");
      }, 1500);
    } catch (error) {
      console.log(error);
      setDeleteStatus("confirm");
    }
  };

  // =====================================
  // ACCESS DENIED
  // =====================================

  if (!canView) {
    return (
      <Container fluid>
        <div className="text-center py-5">
          <h5>Access Denied</h5>
          <p>You don't have permission to view Ferry.</p>
        </div>
      </Container>
    );
  }

  // =====================================
  // RENDER
  // =====================================

  return (
    <>
      <div className="breadcrumb-bar mb-2">
        <div className="breadcrumb-left">
          <Link to="/admin/home" className="breadcrumb-link active">
            Home
          </Link>

          <span className="breadcrumb-separator">|</span>

          <span
            className="breadcrumb-link inactive"
            onClick={() => navigate("/admin/ferry")}
          >
            Ferry
          </span>
        </div>

        {/* ADD BUTTON: EDIT / FULL */}
        {canAdd && (
          <button
            className="add-outlet-btn"
            onClick={() => navigate("/admin/add-ferry")}
          >
            + Add Ferry
          </button>
        )}
      </div>

      <Container fluid>
        <Card className="table-border mb-5">
          <Card.Body>
            <div className="common-table-wrapper">
              <table className="common-table">
                <thead>
                  <tr>
                    <th className="text-center">#</th>
                    <th className="text-center">Source</th>
                    <th className="text-center">Destination</th>

                    {/* ACTION COLUMN ONLY WHEN EDIT/FULL */}
                    {(canEdit || canDelete) && (
                      <th className="text-center">Action</th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    [...Array(6)].map((_, index) => (
                      <tr key={index}>
                        <td className="text-center">
                          <div className="skeleton skeleton-text small"></div>
                        </td>

                        <td className="text-center">
                          <div className="skeleton skeleton-image"></div>
                        </td>

                        <td className="text-center">
                          <div className="skeleton skeleton-image"></div>
                        </td>

                        {(canEdit || canDelete) && (
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-2">
                              <div className="skeleton skeleton-btn"></div>
                              <div className="skeleton skeleton-btn"></div>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : ferryList.length > 0 ? (
                    ferryList.map((ferry, index) => (
                      <tr key={ferry._id}>
                        <td className="text-center">
                          {(currentPage - 1) * pagelimit + index + 1}
                        </td>

                        <td className="text-center">
                          {ferry?.from?.name}
                        </td>

                        <td className="text-center">
                          {ferry?.to?.name}
                        </td>

                        {(canEdit || canDelete) && (
                          <td className="text-center">
                            <div className="d-flex justify-content-center align-items-center gap-3 flex-row">

                              {/* EDIT: EDIT / FULL */}
                              {canEdit && (
                                <button
                                  className="action-btn edit-btn"
                                  onClick={() =>
                                    navigate(
                                      `/admin/edit-ferry/${ferry._id}`
                                    )
                                  }
                                >
                                  <FaEdit size={14} />
                                </button>
                              )}

                              {/* DELETE: FULL ONLY */}
                              {canDelete && (
                                <button
                                  className="action-btn delete-btn"
                                  onClick={() => {
                                    setSelectedId(ferry._id);
                                    setShowDelete(true);
                                  }}
                                >
                                  <FaTrash size={14} />
                                </button>
                              )}

                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={canEdit || canDelete ? 4 : 3}
                        className="text-center py-4"
                      >
                        No ferry records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      </Container>

      {/* DELETE MODAL */}
      {canDelete && (
        <DeleteModal
          show={showDelete}
          onHide={() => setShowDelete(false)}
          onConfirm={handleConfirmDelete}
          status={deleteStatus}
        />
      )}
    </>
  );
}
